import "./formUtils.scss";

import {
  CAlert,
  CBadge,
  CButton,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CInputCheckbox,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CSelect,
  CSwitch,
  CTextarea,
} from "@coreui/react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  getDatasetFolders,
  searchDatasets,
  useClearDataset,
  useCreateDataset,
  useCreateDatasetFolder,
  useCreateRemoteDataset,
  useDatasetFields,
  useDatasetList,
  useDeleteDataset,
  useDeleteDatasetFolder,
  useGetAllDatasetTags,
  useGetDatasetChunkingStrategy,
  useGetDatasetFolders,
  useGetFilteredDatasetTypes,
  useMoveDatasetsToFolder,
  useSyncRemoteDataset,
  useUpdateDataset,
  useUpdateDatasetChunkingStrategy,
  useUpdateDatasetFolder,
} from "src/views/datasets/services/DatasetServices";
import {
  useModelDatasets,
  useModelVersionDatasets,
} from "../models/ModelService";
import { useGetLicensedProducts } from "src/views/managementConsole/services/LicenseService";

import CIcon from "@coreui/icons-react";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Autocomplete from "@mui/material/Autocomplete";
import Switch from "@mui/material/Switch";
import PropTypes from "prop-types";
import LoadingOverlay from "react-loading-overlay-ts";
import { useQuery } from "react-query";
import Select from "react-select";
import { queryClient } from "src/config/react-query";
import { ModalBase } from "./datasetModels/ModelBase";
import { NucToast } from "src/reusable/components/Toast";
import { usePermission } from "src/helpers/Auth";
import { ListSelect } from "src/reusable";
import { DeleteItemModal } from "src/reusable/components/DeleteItemModal";
import TagSelect from "src/reusable/components/select/tag-select";
import { Loading } from "src/reusable/LoadingSpinner";
import { useTagsList } from "src/reusable/services/TagServices";

export const CreateDatasetModal = (props) => {
  const [datasetName, setDatasetName] = React.useState("");
  const [datasetDescription, setDatasetDescription] = React.useState("");
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [datasetType, setDatasetType] = React.useState(null);
  const [datasetChunkingStrategy, setDatasetChunkingStrategy] =
    React.useState(null);
  const [remoteDatasetCredentials, setRemoteDatasetCredentials] =
    React.useState(null);
  const [remoteDatasetURL, setRemoteDatasetURL] = React.useState(null);
  const [datasetFolder, setDatasetFolder] = React.useState(null);
  const { folder_id } = useParams();
  const history = useHistory();
  const {
    mutate: createDataset,
    isLoading: isCreateDatasetLoading,
    status: createDatasetStatus,
    isSuccess: isCreateDatasetSuccess,
    isError: isCreateDatasetError,
    error: createDatasetError,
    data: newDataset,
    reset: resetCreateDataset,
  } = useCreateDataset();

  const { data: datasetFolders } = useGetDatasetFolders();

  const {
    mutate: createRemoteDataset,
    status: createRemoteDatasetStatus,
    isError: isCreateRemoteDatasetError,
    error: createRemoteDatasetError,
    data: newRemoteDataset,
  } = useCreateRemoteDataset();

  const { data: datasetTags, isSuccess: isDatasetTagsSuccess } =
    useGetAllDatasetTags();

  const { mutate: syncRemoteDataset } = useSyncRemoteDataset();

  useEffect(() => {
    if (createRemoteDatasetStatus === "success" && newRemoteDataset) {
      syncRemoteDataset(newRemoteDataset.id);
    }
  }, [createRemoteDatasetStatus, newRemoteDataset, syncRemoteDataset]);

  useEffect(() => {
    if (isDatasetTagsSuccess && datasetTags) {
      setAvailableTags(datasetTags.map((tag) => tag.tag));
    }
  }, [isDatasetTagsSuccess, datasetTags]);

  const { data: datasetChunkingStrategies } = useGetDatasetChunkingStrategy();

  const resetAfterClose = () => {
    setDatasetName("");
    setDatasetDescription("");
    setSelectedTags([]);
    setErrorMessage(null);
    resetCreateDataset();
  };

  const { data: licensedProducts, isError: isLicenseError } =
    useGetLicensedProducts();
  const { data: datasetTypes, isLoading: datasetTypesIsLoading } =
    useGetFilteredDatasetTypes(licensedProducts, isLicenseError);

  // If there was a error creating the dataset, get rid of it!
  if (
    isCreateRemoteDatasetError &&
    String(createRemoteDatasetError?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(createRemoteDatasetError?.response?.data?.detail));
  }
  if (
    isCreateDatasetError &&
    String(createDatasetError?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(createDatasetError?.response?.data?.detail));
  }
  const onSubmit = (name, description, tags, type) => {
    if (name.trim().length === 0) {
      setErrorMessage("Name cannot be empty");
    } else {
      setErrorMessage(null);
      if (remoteDatasetURL !== null && remoteDatasetCredentials !== null) {
        createRemoteDataset({
          name: name.trim(),
          description: description,
          dataset_type_id: type?.id,
          allow_others_to_edit: true,
          chunking_strategy_id: datasetChunkingStrategy?.id,
          remote_dataset: remoteDatasetCredentials && remoteDatasetURL !== null,
          remote_dataset_details: {
            url: remoteDatasetURL,
            credentials: remoteDatasetCredentials,
          },
          dataset_folder_fk: folder_id,
        });
      }
      createDataset({
        name: name.trim(),
        description: description,
        tags: tags,
        dataset_type_id: type.id,
        allow_others_to_edit: true,
        chunking_strategy_id: datasetChunkingStrategy?.id,
        remote_dataset: remoteDatasetCredentials && remoteDatasetURL !== null,
        dataset_folder_fk: folder_id,
        remote_dataset_details: {
          url: remoteDatasetURL,
          credentials: remoteDatasetCredentials,
        },
      });
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  useEffect(() => {
    // if dataset type is document, set the default chunking strategy
    if (datasetType?.name === "document") {
      console.log("Setting default chunking strategy", datasetType?.name);
      setDatasetChunkingStrategy({
        name: "Chunk File with Overlap",
        comment:
          "Break text up into overlapping bits. Create variable token length (default 1024 size chunks with reasonable overlap.) This follows llama index defaults.",
        config: {
          type: "class",
          name: "ChunkFileIntoLLamaIndexDefaults",
        },
        id: -7,
      });
    } else {
      setDatasetChunkingStrategy(null);
    }
  }, [datasetType]);

  useEffect(() => {
    if (props.setDataset && newDataset) {
      props.setDataset({ name: newDataset.name, id: newDataset.id });
    }
  }, [props.setDataset, newDataset, props]);

  useEffect(() => {
    if (isCreateDatasetSuccess) {
      if (datasetFolder) {
        history.push(
          `/datasets/dataset-folder/${datasetFolder.id}/dataset/${newDataset.id}/viewer`,
        );
      } else {
        history.push(`/datasets/${newDataset.id}/viewer`);
      }
    }
  }, [isCreateDatasetSuccess, datasetFolder, newDataset]);

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      id="create-dataset-modal"
    >
      <CModalHeader align-items="center">
        <CCol sm="9">
          <h3>Create a new dataset</h3>
        </CCol>
        <CCol sm="1.5" id="column-text-align-right">
          <CButton
            id="create-dataset-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isCreateDatasetLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating dataset..."
      >
        {createDatasetStatus === "success" ||
        createRemoteDatasetStatus === "success" ? (
          <CModalBody>
            <span id="create-dataset-success-message">
              {newDataset?.name ?? newRemoteDataset?.name} has been created!
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              <CreateNewDatasetFormBody
                disabled={props.disabled}
                datasetType={datasetType}
                setDatasetType={setDatasetType}
                datasetTypesIsLoading={datasetTypesIsLoading}
                datasetName={datasetName}
                errorMessage={errorMessage}
                setDatasetName={setDatasetName}
                setDatasetDescription={setDatasetDescription}
                datasetDescription={datasetDescription}
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
                setAvailableTags={setAvailableTags}
                availableTags={availableTags}
                datasetTypes={datasetTypes}
                datasetChunkingStrategies={datasetChunkingStrategies}
                setDatasetChunkingStrategy={setDatasetChunkingStrategy}
                datasetChunkingStrategy={datasetChunkingStrategy}
                remoteDatasetCredentials={remoteDatasetCredentials}
                setRemoteDatasetCredentials={setRemoteDatasetCredentials}
                remoteDatasetURL={remoteDatasetURL}
                setRemoteDatasetURL={setRemoteDatasetURL}
                datasetFolders={datasetFolders}
                datasetFolder={datasetFolder}
                setDatasetFolder={setDatasetFolder}
              />
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" id="column-text-align-right">
                <CreateNewDatasetButton
                  onSubmit={onSubmit}
                  toggleRemoteDataset={remoteDatasetCredentials}
                  datasetName={datasetName}
                  datasetDescription={datasetDescription}
                  datasetTags={selectedTags}
                  datasetType={datasetType}
                />
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateDatasetModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setDataset: PropTypes.func,
  disabled: PropTypes.bool,
};

export const CreateDatasetFolderModal = (props) => {
  const [folderName, setFolderName] = useState("");
  const [folderDescription, setFolderDescription] = useState("");
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isPublic, setIsPublic] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const EMPTY_FOLDER_NAME_ERROR = "Folder name cannot be empty";
  const EDGE_SPACE_FOLDER_NAME_ERROR =
    "Folder name cannot start or end with a space";

  const trimmedFolderName = useMemo(() => folderName.trim(), [folderName]);

  const hasEdgeFolderNameSpaces = useMemo(
    () => folderName !== trimmedFolderName,
    [folderName, trimmedFolderName],
  );

  const {
    mutate: createDatasetFolder,
    isLoading: isCreateFolderLoading,
    isError: isCreateFolderError,
    error: createFolderError,
    isSuccess: isCreateFolderSuccess,
    reset: resetCreateFolder,
  } = useCreateDatasetFolder();

  const { data: datasetsResponse } = useQuery({
    queryKey: ["datasetlist", "", []],
    queryFn: () => searchDatasets({ pageSize: -1 }), // Get all datasets for the modal (pageSize: -1 returns all)
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const datasets = datasetsResponse?.results || [];

  useEffect(() => {
    if (isCreateFolderSuccess) {
      props.onClose();
    }
  }, [isCreateFolderSuccess]);

  const resetAfterClose = () => {
    setFolderName("");
    setFolderDescription("");
    setSelectedDatasets([]);
    setSelectedRoles([]);
    setErrorMessage(null);
    resetCreateFolder();
  };

  useEffect(() => {
    if (isCreateFolderError) {
      setErrorMessage(createFolderError?.response?.data?.detail);
    }
  }, [isCreateFolderError, createFolderError]);

  const onSubmit = () => {
    if (!trimmedFolderName) {
      setErrorMessage(EMPTY_FOLDER_NAME_ERROR);
    } else if (hasEdgeFolderNameSpaces) {
      setErrorMessage(EDGE_SPACE_FOLDER_NAME_ERROR);
    } else {
      setErrorMessage(null);
      createDatasetFolder({
        name: trimmedFolderName,
        description: folderDescription,
        datasets: selectedDatasets.map((dataset) => dataset.id),
        is_public: isPublic,
        roles: selectedRoles.map((role) => role.id),
      });
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <Dialog open={props.isVisible} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>Create Dataset Folder</Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isCreateFolderLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* ---- Details ---- */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Details
          </Typography>
          <TextField
            required
            label="Folder name"
            value={folderName}
            data-testid="ds-modal-create-folder-input-name"
            fullWidth
            onChange={(e) => {
              const { value } = e.target;
              setFolderName(value);

              if (
                errorMessage === EMPTY_FOLDER_NAME_ERROR ||
                errorMessage === EDGE_SPACE_FOLDER_NAME_ERROR
              ) {
                setErrorMessage(null);
              }
            }}
            error={!!(errorMessage || hasEdgeFolderNameSpaces)}
            helperText={
              hasEdgeFolderNameSpaces
                ? EDGE_SPACE_FOLDER_NAME_ERROR
                : errorMessage
            }
          />
          <TextField
            multiline
            rows={3}
            label="Description"
            fullWidth
            placeholder="Enter a description if needed"
            value={folderDescription}
            onChange={(e) => setFolderDescription(e.target.value)}
          />
        </Box>

        <Divider />

        {/* ---- Contents ---- */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Contents
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            Optionally add existing datasets to this folder now. You can move
            datasets in or out at any time.
          </Typography>
          <Autocomplete
            multiple
            options={datasets || []}
            value={selectedDatasets}
            onChange={(event, newValue) => setSelectedDatasets(newValue)}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField {...params} label="Datasets in this folder" />
            )}
          />
        </Box>

        <Divider />

        {/* ---- Access ---- */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Access
          </Typography>
          <RadioGroup
            value={isPublic ? "public" : "private"}
            onChange={(e) => setIsPublic(e.target.value === "public")}
          >
            <FormControlLabel
              value="public"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">
                    Anyone in the organization
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Everyone can see and browse this folder
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="private"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">
                    Only specific people &amp; groups
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only you can see it until you share it. Share with people or
                    groups after creating, via &quot;Manage access&quot;.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!trimmedFolderName || hasEdgeFolderNameSpaces}
          data-testid="ds-modal-create-folder-btn-submit"
        >
          Create Folder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateDatasetFolderModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Note:
 * modify the disabled prop in this button for dataset based on permission.
 */
export const CreateNewDatasetButton = (props) => {
  return (
    <Button
      id="submit-create-new-dataset"
      color="primary"
      variant="contained"
      onClick={() =>
        props.onSubmit(
          props.datasetName,
          props.datasetDescription,
          props.datasetTags,
          props.datasetType,
        )
      }
      disabled={
        (!props.toggleRemoteDataset && !props.datasetType) ||
        !props.datasetName ||
        props.disabled
      }
    >
      Create New Dataset
    </Button>
  );
};

CreateNewDatasetButton.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  datasetName: PropTypes.string,
  datasetDescription: PropTypes.string,
  datasetTags: PropTypes.array,
  datasetType: PropTypes.object,
  disabled: PropTypes.bool,
  toggleRemoteDataset: PropTypes.bool,
};

export const CreateNewDatasetNoFormBody = (props) => {
  const [toggleRemoteDataset, setToggleRemoteDataset] = React.useState(false);
  const [toggleAdvancedOptions, setToggleAdvancedOptions] =
    React.useState(false);
  const { folder_id } = useParams();

  useEffect(() => {
    if (folder_id) {
      props.setDatasetFolder(
        props.datasetFolders?.find(
          (folder) => folder.id === parseInt(folder_id, 10),
        ),
      );
    }
  }, []);
  return (
    <>
      {props.errorMessage && props.showErrorMessage && (
        <CAlert color="danger">{props.errorMessage}</CAlert>
      )}
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Name</CLabel>
        <CCol sm={10}>
          <CInput
            id="create-new-dataset-name-input"
            type="text"
            name="name"
            placeholder="Name"
            value={props.datasetName ?? ""}
            onChange={(e) => props.setDatasetName(e.target.value.trim())}
          />
        </CCol>
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Description</CLabel>
        <CCol sm={10}>
          <CTextarea
            id="create-new-dataset-description-textarea"
            type="text"
            rows="2"
            name="description"
            placeholder="Description"
            value={props.datasetDescription ?? ""}
            onChange={(e) => props.setDatasetDescription(e.target.value)}
          />
        </CCol>
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Tags</CLabel>
        <CCol sm={10}>
          <TagSelect
            value={props.selectedTags}
            onChange={(newTags) => {
              props.setSelectedTags(newTags);
            }}
            availableTags={props.availableTags}
            setAvailableTags={props.setAvailableTags}
            placeholder="Tags for model"
            allowCreation={true}
            isSmText={true}
          />
        </CCol>
      </CFormGroup>
      <CFormGroup row hidden={props.disabled}>
        <CLabel className="col-sm-2 col-form-label">
          Remote Dataset{" "}
          <CBadge
            color="secondary"
            style={{
              fontSize: "75%",
            }}
          >
            <i>Preview</i>
          </CBadge>
        </CLabel>
        <CCol sm={10}>
          <CSwitch
            id="edit-model-remote-model"
            name="edit-model-remote-model"
            role="switch"
            aria-checked={toggleRemoteDataset}
            aria-label="Remote Model Preview"
            value={toggleRemoteDataset}
            color={toggleRemoteDataset ? "success" : ""}
            onChange={(e) => {
              setToggleRemoteDataset(e.target.checked);
              // reset the dataset type, chunking strategy, url, and credentials
              props.setDatasetType(null);
              props.setDatasetChunkingStrategy(null);
              props.setRemoteDatasetURL(null);
              props.setRemoteDatasetCredentials(null);
            }}
          />
        </CCol>
      </CFormGroup>
      <CFormGroup row hidden={Boolean(!folder_id)}>
        <CLabel className="col-sm-2 col-form-label">Dataset Folder</CLabel>
        <CCol sm={10}>
          <CSelect
            name="datasetFolder"
            value={props.datasetFolder?.id || folder_id}
            disabled={folder_id}
            onChange={(e) => {
              const selectedFolder = props.datasetFolders?.find(
                (folder) => folder.id === parseInt(e.target.value, 10),
              );
              props.setDatasetFolder(selectedFolder);
            }}
            // disabled={props && props.disabled}
          >
            <option value="">Choose a folder for this dataset</option>
            {props.datasetFolders?.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </CSelect>
        </CCol>
      </CFormGroup>
      <CFormGroup row hidden={toggleRemoteDataset}>
        <CLabel className="col-sm-2 col-form-label">Select Dataset Type</CLabel>
        <CCol sm={10}>
          <CSelect
            name="filterOn"
            value={props.datasetType?.name ? props.datasetType.name : "default"}
            onChange={(e) => {
              let dst = props.datasetTypes.filter((_dst) => {
                return e.target.value === _dst.name;
              })[0];
              props.setDatasetType(dst);
            }}
            disabled={props && props.disabled}
          >
            <option value="">Choose a type (Tabular is most Common)</option>
            {!props.datasetTypesIsLoading &&
              props.datasetTypes?.map((dsType) => {
                return (
                  <option key={dsType.id} value={dsType.name}>
                    {dsType.name}
                  </option>
                );
              })}
          </CSelect>
          {props.datasetType && <small>{props.datasetType.description}</small>}
        </CCol>
      </CFormGroup>
      {toggleRemoteDataset && (
        <div style={{ fontSize: "80%", width: "100%" }}>
          <CFormGroup row style={{ display: "flex", justifyContent: "center" }}>
            <CLabel className="col-sm-3 col-form-label">URL</CLabel>
            <CCol sm={8}>
              <CInput
                id="edit-remote-model-url"
                type="text"
                name="remote-model-url"
                placeholder="url"
                value={props.remoteDatasetURL?.url}
                onChange={(e) => props.setRemoteDatasetURL(e.target.value)}
              />
            </CCol>
          </CFormGroup>
          <CFormGroup row style={{ display: "flex", justifyContent: "center" }}>
            <CLabel className="col-sm-3 col-form-label">Credentials</CLabel>
            <CCol sm={8}>
              <CInput
                id="edit-remote-model-credentials"
                type="password"
                name="remote-model-credentials"
                placeholder="credentials"
                value={props.remoteDatasetCredentials}
                onChange={(e) =>
                  props.setRemoteDatasetCredentials(e.target.value)
                }
              />
            </CCol>
          </CFormGroup>
        </div>
      )}
      {(props.datasetType?.name === "document" || toggleRemoteDataset) && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginBottom: "3%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                style={{ marginBottom: "0px" }}
                defaultChecked
                sx={{ m: 1 }}
                size="small"
                checked={toggleAdvancedOptions}
                onChange={(e) => {
                  setToggleAdvancedOptions(e.target.checked);
                }}
              />
            </div>
            <Typography
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "small",
              }}
            >
              Advanced Options
            </Typography>
          </div>
          {toggleAdvancedOptions === true && (
            <CFormGroup row>
              <CLabel className="col-sm-2 col-form-label">
                Select Chunking Strategy
              </CLabel>
              <CCol sm={10}>
                <CSelect
                  name="filterOn"
                  value={props.datasetChunkingStrategy?.name}
                  onChange={(e) => {
                    let dst = props.datasetChunkingStrategies.filter((_dst) => {
                      return e.target.value === _dst.name;
                    })[0];
                    props.setDatasetChunkingStrategy(dst);
                  }}
                  disabled={props && props.disabled}
                >
                  <option value="">Choose a default chunking strategy</option>
                  {!props.datasetTypesIsLoading &&
                    props.datasetChunkingStrategies?.map((dsType) => {
                      return (
                        <option key={dsType.id} value={dsType.name}>
                          {dsType.name}
                        </option>
                      );
                    })}
                </CSelect>
                {props.datasetType && (
                  <small>{props.datasetChunkingStrategy?.comment}</small>
                )}
              </CCol>
            </CFormGroup>
          )}
        </div>
      )}
    </>
  );
};

CreateNewDatasetNoFormBody.propTypes = {
  disabled: PropTypes.bool,
  datasetType: PropTypes.object,
  setDatasetType: PropTypes.func,
  datasetTypesIsLoading: PropTypes.bool,
  datasetName: PropTypes.string,
  errorMessage: PropTypes.string,
  setDatasetName: PropTypes.func,
  setDatasetDescription: PropTypes.func,
  datasetDescription: PropTypes.string,
  setSelectedTags: PropTypes.func,
  selectedTags: PropTypes.array,
  setAvailableTags: PropTypes.func,
  availableTags: PropTypes.array,
  datasetTypes: PropTypes.arrayOf(PropTypes.object),
  datasetChunkingStrategies: PropTypes.arrayOf(PropTypes.object),
  datasetChunkingStrategy: PropTypes.arrayOf(PropTypes.object),
  setDatasetChunkingStrategy: PropTypes.func,
  remoteDatasetCredentials: PropTypes.string,
  setRemoteDatasetCredentials: PropTypes.func,
  remoteDatasetURL: PropTypes.string,
  setRemoteDatasetURL: PropTypes.func,
  showErrorMessage: PropTypes.bool,
  datasetFolders: PropTypes.array,
  datasetFolder: PropTypes.object,
  setDatasetFolder: PropTypes.func,
};

CreateNewDatasetNoFormBody.defaultProps = {
  showErrorMessage: true,
};

export const CreateNewDatasetFormBody = (props) => {
  return (
    <>
      {props.errorMessage && (
        <CAlert color="danger">{props.errorMessage}</CAlert>
      )}
      <CForm>
        <CreateNewDatasetNoFormBody {...props} showErrorMessage={false} />
      </CForm>
    </>
  );
};

CreateNewDatasetFormBody.propTypes = {
  disabled: PropTypes.bool,
  datasetType: PropTypes.object,
  setDatasetType: PropTypes.func,
  datasetTypesIsLoading: PropTypes.bool,
  datasetName: PropTypes.string,
  errorMessage: PropTypes.string,
  setDatasetName: PropTypes.func,
  setDatasetDescription: PropTypes.func,
  datasetDescription: PropTypes.string,
  setSelectedTags: PropTypes.func,
  selectedTags: PropTypes.array,
  setAvailableTags: PropTypes.func,
  availableTags: PropTypes.array,
  datasetTypes: PropTypes.arrayOf(PropTypes.object),
};

export const UpdateDatasetModal = (props) => {
  const [datasetDescription, setDatasetDescription] = React.useState("");
  const [selectedTags, setSelectedTags] = useState(props.datasetTags);
  const [availableTags, setAvailableTags] = useState([]);

  const [datasetAllowOthers, setDatasetAllowOthers] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [newDatasetChunkStrategyId, setNewDatasetChunkStrategyId] =
    React.useState();

  useEffect(() => {
    if (props.datasetTags) {
      setSelectedTags(props.datasetTags);
    }
  }, [props.datasetTags]);

  const { data: tagsList, isSuccess: isTagsSuccess } = useTagsList();
  const {
    mutate: updateDataset,
    isLoading: isUpdateDatasetLoading,
    status: updateDatasetStatus,
    isError: isUpdateDatasetError,
    error: updateDatasetError,
    reset: resetUpdateDataset,
  } = useUpdateDataset();

  const {
    data: datasetChunkingStrategy,
    //isLoading: datasetChunkingStrategyIsLoading,
  } = useGetDatasetChunkingStrategy(props.id);

  const { data: datasetChunkingStrategies } = useGetDatasetChunkingStrategy();

  const { canIt: hasSearchPermission } = usePermission("search:update");

  const {
    mutate: updateDatasetChunkStrategy,
    isError: updateDatasetChunkStrategyIsError,
    error: updateDatasetChunkStrategyError,
  } = useUpdateDatasetChunkingStrategy();

  if (updateDatasetChunkStrategyIsError) {
    alert(
      "Updating chunk strategy failed." +
        updateDatasetChunkStrategyError.response.data.detail,
    );
  }

  useEffect(() => {
    if (isTagsSuccess && tagsList) {
      setAvailableTags(tagsList);
    }
  }, [isTagsSuccess, tagsList]);

  useEffect(() => {
    setDatasetDescription(props.datasetDescription);
    setDatasetAllowOthers(props.datasetAllowOthersToEdit);
  }, [props]);

  const resetAfterClose = () => {
    setDatasetDescription("");
    setDatasetAllowOthers(false);
    setErrorMessage(null);
    resetUpdateDataset();
  };
  // If there was a error creating the dataset, get rid of it!
  if (
    isUpdateDatasetError &&
    String(updateDatasetError?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(updateDatasetError?.response?.data?.detail));
  }
  const onSubmit = (dataset_id, description, tags, allow_others_to_edit) => {
    setErrorMessage(null);

    //only update if we have actually set a defined value
    !!newDatasetChunkStrategyId &&
      updateDatasetChunkStrategy([dataset_id, newDatasetChunkStrategyId]);
    updateDataset({
      id: dataset_id,
      description: description,
      tags: tags,
      allow_others_to_edit: allow_others_to_edit,
    });
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      id="update-dataset-modal"
      centered
      size="lg"
    >
      <CModalHeader id="update-dataset-modal-header">
        <h3>Configure Existing Dataset</h3>
        <CButton
          id="update-dataset-close-button"
          color="secondary"
          onClick={() => onClose()}
          size="sm"
        >
          <CIcon name="cil-x" />
        </CButton>
      </CModalHeader>
      <LoadingOverlay
        active={isUpdateDatasetLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Updating dataset..."
      >
        {updateDatasetStatus === "success" ? (
          <CModalBody>
            <span id="update-dataset-success-message-span">
              Dataset update was successful.
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Name</CLabel>
                  <CLabel
                    id="update-dataset-name-label"
                    className="col-sm-10 col-form-label"
                  >
                    {props.datasetName}
                  </CLabel>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm={10}>
                    <CTextarea
                      id="update-dataset-description-textarea"
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={
                        datasetDescription === null ? "" : datasetDescription
                      }
                      onChange={(e) => setDatasetDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">Tags</CLabel>
                  <CCol sm={10}>
                    <TagSelect
                      value={selectedTags}
                      onChange={(newTags) => {
                        setSelectedTags(newTags);
                      }}
                      availableTags={availableTags}
                      setAvailableTags={setAvailableTags}
                      placeholder="Tags for dataset"
                      allowCreation={true}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-2 col-form-label">
                    Search Strategy
                  </CLabel>
                  {/* Dropdown to display the data chunking strategies */}
                  <CCol sm={10}>
                    <CSelect
                      id="update-dataset-search-strategy-dropdown"
                      name="search_strategy"
                      value={datasetChunkingStrategy?.id}
                      onChange={(e) => {
                        console.log(
                          "Updating dataset chunking strategy to " +
                            e.target.value +
                            ".",
                        );
                        setNewDatasetChunkStrategyId(e.target.value);
                      }}
                      disabled={!hasSearchPermission}
                    >
                      {datasetChunkingStrategy &&
                        datasetChunkingStrategies?.map((strategy) => (
                          <option
                            key={strategy.id}
                            value={strategy.id}
                            selected={
                              datasetChunkingStrategy.length > 0 &&
                              strategy.id === datasetChunkingStrategy[0].id
                            }
                          >
                            {strategy.name}
                          </option>
                        ))}
                    </CSelect>
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel
                    className="col-sm-2 col-form-label"
                    htmlFor="allow_others_to_edit"
                  >
                    Allow others to edit
                  </CLabel>
                  <CCol sm={10}>
                    <CInputCheckbox
                      id="update-dataset-allow-others-checkbox"
                      type="checkbox"
                      name="allow_others_to_edit"
                      checked={!!datasetAllowOthers}
                      onChange={(e) => setDatasetAllowOthers(e.target.checked)}
                    />
                  </CCol>
                </CFormGroup>
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" id="column-text-align-right">
                <CButton
                  id="update-dataset-submit-button"
                  color="success"
                  onClick={() =>
                    onSubmit(
                      props.id,
                      datasetDescription,
                      selectedTags,
                      datasetAllowOthers,
                      newDatasetChunkStrategyId,
                    )
                  }
                >
                  Update Dataset
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

UpdateDatasetModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  datasetName: PropTypes.string,
  datasetDescription: PropTypes.string,
  datasetTags: PropTypes.array,
  datasetAllowOthersToEdit: PropTypes.bool,
};

export const DeleteDatasetModal = (props) => {
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: deleteDataset,
    isLoading: isDeleteDatasetLoading,
    status: deleteDatasetStatus,
    isError: isDeleteDatasetError,
    error: deleteDatasetError,
    reset: resetDeleteDataset,
  } = useDeleteDataset();

  const {
    mutate: deleteDatasetFolder,
    isLoading: isDeleteDatasetLoadingFolder,
    isSuccess: isDeleteDatasetSuccessFolder,
    status: deleteDatasetStatusFolder,
    isError: isDeleteDatasetErrorFolder,
    error: deleteDatasetErrorFolder,
    reset: resetDeleteDatasetFolder,
  } = useDeleteDatasetFolder();

  useEffect(() => {
    if (isDeleteDatasetSuccessFolder) {
      props.onClose();
    }
  }, [isDeleteDatasetSuccessFolder, props]);

  const resetAfterClose = () => {
    setConfirmDelete("");
    setErrorMessage(null);
    resetDeleteDataset();
    resetDeleteDatasetFolder();
  };

  // If there was a error deleting the dataset folder, show message
  if (
    isDeleteDatasetErrorFolder &&
    String(deleteDatasetErrorFolder?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(deleteDatasetErrorFolder?.response?.data?.detail));
  }

  // If there was a error deleting the dataset, show message
  if (
    isDeleteDatasetError &&
    String(deleteDatasetError?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(deleteDatasetError?.response?.data?.detail));
  }
  const onSubmit = (datasetId) => {
    setErrorMessage(null);
    if (props.isFolder) {
      deleteDatasetFolder(props.dataset.id);
      return;
    }
    deleteDataset({
      id: datasetId,
    });
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <DeleteItemModal
      isVisible={props.isVisible}
      onClose={onClose}
      itemName={props.datasetName}
      itemId={props.id}
      onSubmit={onSubmit}
      confirmDeleteString={confirmDelete}
      setConfirmDeleteString={setConfirmDelete}
      errorMessage={errorMessage}
      deleteItemStatus={deleteDatasetStatus || deleteDatasetStatusFolder}
      isDeleteItemLoading={
        isDeleteDatasetLoading || isDeleteDatasetLoadingFolder
      }
      modalTitle={
        !props.isFolder
          ? "Delete Existing Dataset"
          : "Delete Existing Dataset Folder"
      }
      deleteInstructions={
        !props.isFolder
          ? "CONFIRM YOU WANT TO DELETE THIS DATASET BY TYPING DATASET NAME HERE, THEN CLICK DELETE"
          : "CONFIRM YOU WANT TO DELETE THIS DATASET FOLDER BY TYPING DATASET FOLDER NAME HERE, THEN CLICK DELETE"
      }
      buttonTitle={!props.isFolder ? "Delete Dataset" : "Delete Dataset Folder"}
      deleteDatasetsInFolderCountText={
        props.isFolder && props.dataset.datasets.length > 0
          ? `This will delete ${props.dataset.datasets.length} dataset(s) inside the folder`
          : null
      }
    />
  );
};

DeleteDatasetModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  datasetName: PropTypes.string,
  id: PropTypes.number,
  dataset: PropTypes.object,
  isFolder: PropTypes.bool,
  errorMessage: PropTypes.string,
  deleteDatasetsInFolderCountText: PropTypes.string,
};

export const ClearDatasetModal = (props) => {
  const [confirmClear, setConfirmClear] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: clearDataset,
    isLoading: isClearDatasetLoading,
    status: clearDatasetStatus,
    isError: isClearDatasetError,
    error: clearDatasetError,
    data: clearDatasetResponse,
    reset: resetClearDataset,
  } = useClearDataset();

  const {
    mutate: clearDatasetFolder,
    isLoading: isClearDatasetLoadingFolder,
    isSuccess: isClearDatasetSuccessFolder,
    status: clearDatasetStatusFolder,
    isError: isClearDatasetErrorFolder,
    error: clearDatasetErrorFolder,
    reset: resetClearDatasetFolder,
  } = useUpdateDatasetFolder();

  useEffect(() => {
    if (isClearDatasetSuccessFolder) {
      queryClient.invalidateQueries("datasetList");
      queryClient.invalidateQueries("datasetFolders");
      props.onClose();
    }
  }, [isClearDatasetSuccessFolder, props]);

  const resetAfterClose = () => {
    setConfirmClear("");
    setErrorMessage(null);
    resetClearDataset();
    resetClearDatasetFolder();
  };

  // If there was a error clearing the dataset folder, show message
  if (
    isClearDatasetErrorFolder &&
    String(clearDatasetErrorFolder?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(clearDatasetErrorFolder?.response?.data?.detail));
  }

  // If there was a error deleting the dataset, show message
  if (
    isClearDatasetError &&
    String(clearDatasetError?.response?.data?.detail) !== errorMessage
  ) {
    setErrorMessage(String(clearDatasetError?.response?.data?.detail));
  }
  const onSubmit = (datasetId) => {
    setErrorMessage(null);
    if (props.isFolder) {
      clearDatasetFolder({
        id: props.dataset.id,
        name: props.dataset.name,
        description: props.dataset.description,
        datasets: [],
        is_public: props.dataset.is_public,
        roles: props.dataset.roles.map((role) => role.id),
      });
    } else {
      clearDataset(datasetId);
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(props.datasetName);
    NucToast({ message: "Copied to clipboard!", type: "info" });
  };

  return (
    <ModalBase
      title={`Clear the data from an existing ${
        props.isFolder ? "dataset folder" : "dataset"
      }`}
      isVisible={props.isVisible}
      onClose={onClose}
      closeButtonTestId="ds-modal-clear-btn-close"
    >
      <LoadingOverlay
        active={isClearDatasetLoading || isClearDatasetLoadingFolder}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Clearing dataset..."
      >
        {clearDatasetStatus === "success" ||
        clearDatasetStatusFolder === "success" ? (
          <span id="clear-dataset-success-message">
            {clearDatasetResponse?.status === 202
              ? `${props.datasetName} ${
                  clearDatasetResponse.data?.task_id
                    ? "is being cleared in the background"
                    : "is already being cleared"
                } (${Number(
                  clearDatasetResponse.data?.record_count ?? 0,
                ).toLocaleString()} records). It will empty over the next few minutes; you can close this window.`
              : `${props.datasetName} has been cleared`}
          </span>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Alert severity="warning" id="clear-dataset-warning-message-1">
              <Typography variant="body2">
                Are you sure you want to clear the data from{" "}
                <strong>{props.datasetName}</strong>? This action is{" "}
                <strong>non-reversible</strong>!
                {!props.isFolder ? (
                  <>
                    {" "}
                    You will lose all data from and will also lose any
                    associated data from <strong>Test Runs</strong>,{" "}
                    <strong>Scored Predictions</strong>, or{" "}
                    <strong>any other data</strong> that is linked to this
                    dataset.
                  </>
                ) : null}
              </Typography>
            </Alert>

            <Box sx={{ mt: -1.5 }}>
              <Typography
                variant="body1"
                align="center"
                sx={{ mb: 0.5, fontSize: "1.05rem", fontWeight: 500 }}
              >
                Type the name to confirm:
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Tooltip title="Copy Text">
                  <strong
                    onClick={handleCopyToClipboard}
                    className="clickable-copy-text"
                    data-testid="ds-modal-clear-name"
                    style={{ fontSize: "1rem" }}
                  >
                    <ContentCopyIcon
                      sx={{
                        width: "18px",
                        height: "18px",
                        verticalAlign: "text-bottom",
                        mr: 0.5,
                      }}
                    />
                    {props.datasetName}
                  </strong>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                name="name"
                sx={{ mt: 1 }}
                placeholder={`${
                  props.isFolder ? "Dataset Folder" : "Dataset"
                } Name`}
                value={confirmClear}
                onChange={(e) => setConfirmClear(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  id: "clear-dataset-confirm-datasetname-input",
                  "data-testid": "ds-modal-clear-input-confirm",
                }}
              />
            </Box>

            <Button
              id="clear-dataset-submit-button"
              data-testid="ds-modal-clear-btn-submit"
              variant="contained"
              color="error"
              disabled={confirmClear !== props.datasetName}
              onClick={() => onSubmit(props.id)}
              sx={{ width: "fit-content", ml: "auto" }}
            >
              Clear {props.isFolder ? "Dataset Folder" : "Dataset"}
            </Button>
          </Box>
        )}
      </LoadingOverlay>
    </ModalBase>
  );
};

ClearDatasetModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  datasetName: PropTypes.string,
  id: PropTypes.number,
  isFolder: PropTypes.bool,
  dataset: PropTypes.object,
};

export const DatasetSelect = (props) => {
  const [datasetId, setDatasetId] = useState(props.datasetId);
  useEffect(() => {
    setDatasetId(props.datasetId);
    if (props.setProjectGeniusProject) {
      props.setProjectGeniusProject(
        props.datasetId !== null && props.datasetId !== "",
      );
    }
  }, [props.datasetId]);
  useEffect(() => {
    props.handleSelectedDataset(datasetId);
    if (props.setProjectGeniusProject) {
      props.setProjectGeniusProject(datasetId !== null && datasetId !== "");
    }
  }, [datasetId, props]);

  const { data: datasetList } = useDatasetList(
    props.logicallyDeleted,
    props.datasetTypes,
  );
  let displayItems = [];
  if (datasetList) {
    displayItems = datasetList;
  }
  return (
    <ListSelect
      selectedValue={datasetId}
      handleSelectedValue={setDatasetId}
      value={datasetId}
      items={displayItems}
      selectDomID="dataset-list-select"
      disabled={!datasetList || props.disabled}
    />
  );
};

DatasetSelect.defaultProps = {
  logicallyDeleted: false,
  datasetTypes: null,
};

DatasetSelect.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleSelectedDataset: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  logicallyDeleted: PropTypes.bool,
  datasetTypes: PropTypes.number,
  setProjectGeniusProject: PropTypes.func,
};

DatasetSelect.defaultProps = {
  datasetId: null,
  disabled: false,
};
export const ModelDatasetSelect = (props) => {
  const [datasetId, setDatasetId] = useState(props.datasetId);

  useEffect(() => {
    setDatasetId(props.datasetId);
  }, [props.datasetId]);

  useEffect(() => {
    props.handleSelectedDataset(datasetId);
  }, [datasetId, props]);

  //union of all datasets associated with all models
  // Need api route and service call to get this
  const { data: modelsDatasetsList } = useModelDatasets(props.modelIds);

  let displayItems = [];
  if (modelsDatasetsList) {
    displayItems = modelsDatasetsList;
  }

  return (
    <ListSelect
      selectedValue={datasetId}
      handleSelectedValue={setDatasetId}
      value={datasetId}
      items={displayItems}
      selectDomID="model-dataset-list-select"
      disabled={!modelsDatasetsList || props.disabled}
    />
  );
};

ModelDatasetSelect.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // array of numbers or strings
  modelIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  handleSelectedDataset: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ModelDatasetSelect.defaultProps = {
  datasetId: null,
  modelIds: null,
  disabled: false,
};

DatasetSelect.defaultProps = {
  datasetId: null,
  disabled: false,
};
export const ModelVersionDatasetSelect = (props) => {
  // all datasets associated with a specified model version
  const { data: modelVersionDatasets } = useModelVersionDatasets(
    props.modelId,
    props.modelVersionNum,
  );

  const [selectedValue, setSelectedValue] = useState(null);

  const getDefaultValue = () => {
    if (props.datasetId && modelVersionDatasets) {
      const matchingDataset = modelVersionDatasets.find(
        (item) => item.id === props.datasetId,
      );
      if (matchingDataset) {
        return {
          value: matchingDataset.id,
          label: matchingDataset.name,
        };
      }
    }
    return null;
  };

  useEffect(() => {
    setSelectedValue(getDefaultValue());
  }, [props.datasetId, modelVersionDatasets]);

  return (
    <Select
      id="model-version-dataset-select"
      isDisabled={!modelVersionDatasets || props.disabled}
      value={selectedValue}
      onChange={(e) => {
        setSelectedValue(e);
        props.handleSelectedDataset(e.value);
      }}
      options={modelVersionDatasets?.map((item) => ({
        value: item.id,
        label: item.name,
      }))}
      noOptionsMessage={() => "No Datasets found"}
      // Portal the menu to <body> so it isn't clipped by ancestors with
      // overflow: hidden (e.g. the MUI Card in ManualAutoFlowCard)
      menuPortalTarget={document.body}
      // The portaled menu is positioned at open time, so it detaches if a
      // scrollable ancestor scrolls (JobsCreateAndUpdate renders this inside
      // an overflow:auto column) — close it then, but not when the user is
      // scrolling the option list itself
      classNamePrefix="model-version-dataset-select"
      closeMenuOnScroll={(e) =>
        e.target instanceof Element &&
        !e.target.classList.contains("model-version-dataset-select__menu-list")
      }
      styles={{
        menuList: (base) => ({ ...base, maxHeight: "25vh" }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
    />
  );
};

ModelVersionDatasetSelect.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modelId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modelVersionNum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleSelectedDataset: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ModelVersionDatasetSelect.defaultProps = {
  datasetId: null,
  modelId: null,
  modelVersionNum: null,
  disabled: false,
};

export const DatasetFieldSelect = (props) => {
  const [datasetField, setDatasetField] = useState(props.datasetField);
  useEffect(() => {
    setDatasetField(props.datasetField);
  }, [props.datasetField]);
  useEffect(() => {
    props.handleSelectedDatasetField(datasetField);
  }, [datasetField, props]);

  const { data: datasetFields } = useDatasetFields(props.datasetId);

  let displayItems = [];
  if (datasetFields) {
    displayItems = datasetFields;
  }
  const idKey = props.useNameForValue ? "field_name" : "id";
  return (
    <ListSelect
      selectedValue={datasetField}
      handleSelectedValue={setDatasetField}
      items={displayItems}
      selectDomID="datasetfield-list-select"
      itemNameKeyIs="field_name"
      itemIdKeyIs={idKey}
      disabled={!datasetFields || props.disabled}
    />
  );
};

DatasetFieldSelect.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  handleSelectedDatasetField: PropTypes.func.isRequired,
  useNameForValue: PropTypes.bool,
  datasetField: PropTypes.any,
  disabled: PropTypes.bool,
};

DatasetFieldSelect.defaultProps = {
  useNameForValue: false,
  datasetField: null,
  disabled: false,
};

export const UpdateDatasetFolderModal = (props) => {
  const [folderName, setFolderName] = useState(props?.dataset?.name);
  const [folderDescription, setFolderDescription] = useState(
    props?.dataset?.description,
  );
  const [selectedDatasets, setSelectedDatasets] = useState(
    props?.dataset?.datasets || [],
  );
  const [selectedRoles, setSelectedRoles] = useState(
    props?.dataset?.roles || [],
  );
  const [isPublic, setIsPublic] = useState(props?.dataset?.is_public);
  const [errorMessage, setErrorMessage] = useState(null);
  const [removedDatasets, setRemovedDatasets] = useState([]);

  const {
    mutate: updateDatasetFolder,
    isLoading,
    isError: updateDatasetFolderError,
    error: updateDatasetFolderErrorData,
  } = useUpdateDatasetFolder();

  const { data: allDatasetsResponse } = useQuery({
    queryKey: ["datasetlist"],
    queryFn: () => searchDatasets({ pageSize: -1 }), // Get all datasets for the modal (pageSize: -1 returns all)
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const allDatasets = allDatasetsResponse?.results || [];

  useEffect(() => {
    if (updateDatasetFolderError) {
      setErrorMessage(updateDatasetFolderErrorData?.response?.data?.detail);
    }
  }, [updateDatasetFolderError]);

  const handleDatasetChange = (event, newValue) => {
    if (selectedDatasets.length > newValue.length) {
      const removedDataset = selectedDatasets.find(
        (dataset) => !newValue.some((d) => d.id === dataset.id),
      );
      if (removedDataset) {
        setRemovedDatasets([...removedDatasets, removedDataset]);
      }
    }
    setSelectedDatasets(newValue);
  };

  useEffect(() => {
    setFolderName(props?.dataset?.name || "");
    setFolderDescription(props?.dataset?.description || "");
    setSelectedDatasets(props?.dataset?.datasets || []);
    setSelectedRoles(props?.dataset?.roles || []);
    setIsPublic(props?.dataset?.is_public || false);
    setRemovedDatasets([]);
    setErrorMessage(null);
  }, [props.dataset]);

  const resetAfterClose = () => {
    setFolderName("");
    setFolderDescription("");
    setSelectedDatasets([]);
    setSelectedRoles([]);
    setRemovedDatasets([]);
    setErrorMessage(null);
  };

  const onSubmit = () => {
    if (!folderName.trim()) {
      setErrorMessage("Folder name cannot be empty");
      return;
    }

    setErrorMessage(null);

    updateDatasetFolder(
      {
        id: props.folderId,
        name: folderName,
        description: folderDescription,
        datasets: selectedDatasets.map((dataset) => dataset.id),
        is_public: isPublic,
        roles: selectedRoles.map((role) => role.id),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries("datasetFolders");
          queryClient.invalidateQueries("datasetList");
          props.onClose();
        },
      },
    );
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  const getAvailableDatasets = () => {
    const datasetMap = new Map();

    allDatasets.forEach((dataset) => {
      datasetMap.set(dataset.id, dataset);
    });

    removedDatasets.forEach((dataset) => {
      datasetMap.set(dataset.id, dataset);
    });

    return Array.from(datasetMap.values());
  };

  return (
    <Dialog open={props.isVisible} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>Update Dataset Folder</Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <TextField
          required
          label="Folder Name"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          disabled={isLoading}
          error={!!errorMessage}
          helperText={errorMessage}
          data-testid="ds-modal-update-folder-input-name"
        />

        <TextField
          multiline
          rows={3}
          label="Description"
          value={folderDescription}
          onChange={(e) => setFolderDescription(e.target.value)}
          disabled={isLoading}
        />

        <Autocomplete
          multiple
          options={getAvailableDatasets()}
          value={selectedDatasets}
          onChange={handleDatasetChange}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField {...params} label="Datasets Inside of Folder" />
          )}
          disabled={isLoading}
        />

        <FormHelperText>
          Visibility and sharing are managed from &quot;Manage access&quot;.
        </FormHelperText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isLoading}
          data-testid="ds-modal-update-folder-btn-submit"
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UpdateDatasetFolderModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folderId: PropTypes.number,
  dataset: PropTypes.object,
};

export const DatasetFolderMappingModal = (props) => {
  const [selectedDatasetId, setSelectedDatasetId] = useState(props.id);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const { folder_id } = useParams();
  const { data: allFolders } = useQuery(["datasetFolders"], getDatasetFolders, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  useEffect(() => {
    setSelectedDatasetId(props.id);
  }, [props]);
  const {
    mutate: moveDatasetsToFolder,
    status: updateMoveDatasetsToFolderStatus,
    error: updateMoveDatasetsToFolderError,
    isLoading: updateMoveDatasetsToFolderIsLoading,
  } = useMoveDatasetsToFolder();

  const resetAfterClose = () => {
    setSelectedDatasetId(null);
    setSelectedFolder(null);
    setErrorMessage(null);
  };

  useEffect(() => {
    if (updateMoveDatasetsToFolderStatus === "error") {
      setErrorMessage(updateMoveDatasetsToFolderError?.response?.data?.detail);
    }
  }, [updateMoveDatasetsToFolderStatus]);

  const onSubmit = () => {
    if (!selectedDatasetId || !selectedFolder) {
      setErrorMessage("Dataset and Folder are required");
      return;
    }

    setErrorMessage(null);

    moveDatasetsToFolder({
      oldDatasetFolderId: folder_id ?? null,
      newDatasetFolderId: selectedFolder.id,
      datasetIds: [selectedDatasetId],
    });
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  useEffect(() => {
    if (updateMoveDatasetsToFolderStatus === "success") {
      onClose();
    }
  }, [updateMoveDatasetsToFolderStatus]);
  return (
    <Dialog
      open={props.isVisible}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{
        // Add transition props to prevent flickering
        timeout: {
          enter: 300,
          exit: 200,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Move Dataset to Folder</Typography>
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogTitle>
      {/* Rest of the modal content */}
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2, mt: 2 }}
      >
        <Autocomplete
          sx={{ mt: 1 }}
          options={[{ id: null, name: "None" }, ...(allFolders || [])]}
          value={selectedFolder}
          onChange={(e, newValue) => setSelectedFolder(newValue)}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} label="Folder" />}
          disabled={updateMoveDatasetsToFolderIsLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={updateMoveDatasetsToFolderIsLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={updateMoveDatasetsToFolderIsLoading}
        >
          Move to Folder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DatasetFolderMappingModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  datasetName: PropTypes.string,
  datasetFolders: PropTypes.array,
};
